import { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, KpiCard } from "@/components/Bits";
import { Field, inputCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, inrShort, num, dmy, today } from "@/lib/format";

const PIE_COLORS = ["#EA580C", "#0F172A", "#0891B2", "#15803D", "#B45309", "#7C3AED", "#DC2626", "#64748B"];

// Module / Source filter. Keys mirror the backend `module` values exactly, so the
// screen, the Excel export and the CSV export always describe the same population.
const MODULES = [
  { value: "all", label: "All modules" },
  { value: "opd", label: "OPD" },
  { value: "ipd", label: "IPD" },
  { value: "packages", label: "Special Packages / Treatments" },
  { value: "maternity", label: "Maternity" },
  { value: "birth", label: "Birth Records" },
];

export default function HospitalReports() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [loc, setLoc] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [module, setModule] = useState("all");
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(today());

  const load = useCallback(async () => {
    setD(null); setLoc(null);
    try {
      const params = { from_date: from || undefined, to_date: to || undefined,
                       module: module || "all",
                       doctor_id: doctorId || undefined,
                       department_id: departmentId || undefined };
      const [rep, locations] = await Promise.all([
        api.get("/hospital/reports", { params }),
        api.get("/hospital/reports/locations", {
          params: { ...params, doctor_id: doctorId || undefined, department_id: departmentId || undefined } }),
      ]);
      setD(rep.data); setLoc(locations.data);
    } catch (e) { setError(apiError(e)); }
  }, [from, to, doctorId, departmentId, module]);

  useEffect(() => {
    if (!can("hospital.reports.view")) return;
    load();
    Promise.all([api.get("/hospital/doctors"), api.get("/hospital/departments")])
      .then(([doc, dep]) => { setDoctors(doc.data.items); setDepartments(dep.data.items); })
      .catch(() => {});
  }, [load, can]);

  if (!can("hospital.reports.view")) return <NoAccess what="hospital reports" />;

  const exportCsv = () => {
    const rows = [
      ["Module", module || "all"],
      ["From", d.from], ["To", d.to],
      [],
      ["Date", "Revenue"],
      ...d.daily.map((r) => [r.date, r.amount]),
    ];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `hospital-revenue-${module}-${d.from}-to-${d.to}.csv`;
    a.click();
  };

  return (
    <div data-testid="hospital-reports-page">
      <PageHeader overline="Hospital" title="Reports & analytics"
        subtitle="Revenue, collections, clinical volumes and patient-origin analytics for any date range."
        actions={can("hospital.reports.export") && d && (
          <>
            <button onClick={async () => {
              try {
                const res = await api.get("/hospital/reports/export", {
                  params: { from_date: from || undefined, to_date: to || undefined, fmt: "xlsx",
                            module: module || "all" },
                  responseType: "blob" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(res.data);
                a.download = `hospital-report-${module}-${d.from}-to-${d.to}.xlsx`;
                a.click();
              } catch (e) { setError(apiError(e)); }
            }} className={ghostBtnCls} data-testid="hospital-export-xlsx-btn">Export Excel</button>
            <button onClick={exportCsv} className={ghostBtnCls} data-testid="hospital-export-csv-btn">Export CSV</button>
          </>
        )} />

      <div className="flex flex-wrap items-end gap-4 mb-8">
        <Field label="From"><input type="date" data-testid="report-from-input" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" data-testid="report-to-input" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Doctor (location filter)">
          <select className={inputCls} value={doctorId} data-testid="report-doctor-filter" onChange={(e) => setDoctorId(e.target.value)}>
            <option value="" label="All doctors" />
            {doctors.map((x) => <option key={x.doctor_id} value={x.doctor_id} label={`Dr. ${x.name}`} />)}
          </select>
        </Field>
        <Field label="Department (location filter)">
          <select className={inputCls} value={departmentId} data-testid="report-department-filter" onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="" label="All departments" />
            {departments.map((x) => <option key={x.department_id} value={x.department_id} label={x.name} />)}
          </select>
        </Field>
        <Field label="Module / Source">
          <select className={inputCls} value={module} data-testid="report-module-filter"
            onChange={(e) => setModule(e.target.value)}>
            {MODULES.map((m) => <option key={m.value} value={m.value} label={m.label} />)}
          </select>
        </Field>
      </div>
      {module !== "all" && d && (
        <p className="-mt-4 mb-8 text-xs text-slate-500" data-testid="report-module-note">
          Filtered to <strong>{MODULES.find((m) => m.value === module)?.label}</strong>
          {d.supported === false ? " — this module has no billing source yet, so figures show as zero rather than estimated." : ""}.
        </p>
      )}

      {error && <ErrorState message={error} />}
      {!d ? <Loading /> : (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <KpiCard label="Revenue" value={inrShort(d.revenue)} tone="accent" onClick={() => {}} testid="report-revenue" />
            <KpiCard label="Collected" value={inrShort(d.collected)} tone="success" onClick={() => {}} testid="report-collected" />
            <KpiCard label="Outstanding" value={inrShort(d.outstanding)} tone="danger" onClick={() => {}} testid="report-outstanding" />
            <KpiCard label="Invoices" value={num(d.invoice_count)} onClick={() => {}} testid="report-invoices" />
            <KpiCard label="New patients" value={num(d.new_patients)} onClick={() => {}} testid="report-new-patients" />
            <KpiCard label="OPD visits" value={num(d.opd_visits)} onClick={() => {}} testid="report-opd" />
            <KpiCard label="Admissions" value={num(d.admissions)} onClick={() => {}} testid="report-admissions" />
            <KpiCard label="Appointments" value={num(d.appointments)} onClick={() => {}} testid="report-appointments" />
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="panel">
              <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">Revenue by type</h3></div>
              <div className="p-6 space-y-3 text-sm">
                {Object.keys(d.revenue_by_type).length === 0 ? <p className="text-slate-400">No billing in this range.</p> :
                  Object.entries(d.revenue_by_type).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="capitalize text-slate-600">{k}</span><span className="font-medium">{inr(v)}</span></div>
                  ))}
              </div>
            </div>
            <div className="panel">
              <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">Daily revenue</h3></div>
              <div className="p-6 space-y-2 text-sm max-h-80 overflow-y-auto">
                {d.daily.length === 0 ? <p className="text-slate-400">No billing in this range.</p> :
                  d.daily.map((r) => (
                    <div key={r.date} className="flex justify-between"><span className="text-slate-600">{dmy(r.date)}</span><span className="font-medium">{inr(r.amount)}</span></div>
                  ))}
              </div>
            </div>
          </div>

          <div className="mt-10" data-testid="location-analytics">
            <h2 className="font-ui text-lg font-bold tracking-tight text-slate-900">Location analytics</h2>
            <p className="mt-2 text-sm text-slate-500">
              Where your patients come from, built from the structured address fields on each patient record
              {loc ? ` · ${num(loc.total_patients)} patient(s) in range` : ""}.
            </p>
            {!loc ? <Loading label="Loading location analytics…" /> : loc.total_patients === 0 ? (
              <div className="panel mt-5">
                <EmptyState title="No patient locations yet"
                  subtitle="Register patients with city and state to see the distribution here." testid="location-empty" />
              </div>
            ) : (
              <div className="mt-5 grid gap-6 lg:grid-cols-2">
                <RankChart title="Top cities" rows={loc.cities} testid="chart-cities" />
                <RankChart title="Top states" rows={loc.states} testid="chart-states" />
                <div className="panel">
                  <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">Country share</h3></div>
                  <div className="p-6 h-72" data-testid="chart-countries">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <PieChart>
                        <Pie data={loc.countries.slice(0, 8)} dataKey="count" nameKey="name" innerRadius="45%" outerRadius="78%" paddingAngle={2}>
                          {loc.countries.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v, n) => [`${v} patient(s)`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="border-t border-slate-100 px-6 py-4 text-sm">
                    {loc.countries.slice(0, 8).map((c, i) => (
                      <li key={c.name} className="flex items-center justify-between py-1">
                        <span className="flex items-center gap-2 text-slate-600">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          {c.name}
                        </span>
                        <span className="font-medium">{c.count} · {c.percent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <RankChart title="Top districts" rows={loc.districts} testid="chart-districts" />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const RankChart = ({ title, rows, testid }) => {
  const data = (rows || []).slice(0, 8);
  return (
    <div className="panel">
      <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">{title}</h3></div>
      <div className="p-6 h-72" data-testid={testid}>
        {data.length === 0 ? <p className="text-sm text-slate-400">Not enough data.</p> : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
            <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid horizontal={false} stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#334155" }} />
              <Tooltip formatter={(v, _n, p) => [`${v} patient(s) · ${p.payload.percent}%`, p.payload.name]} />
              <Bar dataKey="count" fill="#EA580C" radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
